import { useDatabaseSelect, useDatabaseDelete } from '@/store/slices/databaseSlice';
import { Row_projects, Row_issues, Row_issue_favorites, UUID } from '@/types/databaseRows';
import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { ChevronRight, X } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useExpandedProjects } from '@/hooks/useExpandedProjects';
import { useExpandedFavorites } from '@/hooks/useExpandedFavorites';
import { useAuth } from '@/contexts/AuthProvider';
import IssueInlinePreview from '@/components/IssueInlinePreview';
import { getIssueUrl } from '@/lib/issueUtils';

export default function ProjectIssuesSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Track which projects are expanded with localStorage persistence
  const { expandedProjects, toggleProject } = useExpandedProjects();
  const { expandedFavorites, toggleFavorites } = useExpandedFavorites();

  // Database mutation for removing favorites
  const { deleteRows } = useDatabaseDelete();

  // Fetch all projects
  const { data: projects, isLoading: projectsLoading } = useDatabaseSelect<Row_projects>({
    table: 'projects',
  });

  // Fetch all issues
  const { data: allIssues, isLoading: issuesLoading } = useDatabaseSelect<Row_issues>({
    table: 'issues',
  });

  // Fetch user's favorites
  const { data: userFavorites, isLoading: favoritesLoading } = useDatabaseSelect<Row_issue_favorites>({
    table: 'issue_favorites',
    filters: user?.id ? [{ type: 'eq', column: 'user_id', value: user.id as UUID }] : [],
  });

  // Filter out done and canceled issues
  const issues = useMemo(() => {
    if (!allIssues) return [];
    return allIssues.filter(issue =>
      issue.status !== 'done' && issue.status !== 'canceled'
    );
  }, [allIssues]);

  // Group issues by project
  const issuesByProject = useMemo(() => {
    if (!issues) return {};

    const grouped: Record<string, Row_issues[]> = {};
    issues.forEach(issue => {
      if (issue.project_id) {
        if (!grouped[issue.project_id]) {
          grouped[issue.project_id] = [];
        }
        grouped[issue.project_id].push(issue);
      }
    });

    // Sort issues by last_activity_at (most recent first)
    Object.keys(grouped).forEach(projectId => {
      grouped[projectId].sort((a, b) => {
        const aTime = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
        const bTime = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
        return bTime - aTime;
      });
    });

    return grouped;
  }, [issues]);

  // Get favorited issues
  const favoriteIssues = useMemo(() => {
    if (!userFavorites || !allIssues) return [];
    
    const favoriteIssueIds = new Set(userFavorites.map(fav => fav.issue_id));
    return allIssues
      .filter(issue => favoriteIssueIds.has(issue.id))
      .filter(issue => issue.status !== 'done' && issue.status !== 'canceled')
      .sort((a, b) => {
        const aTime = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
        const bTime = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
        return bTime - aTime; // Most recent first
      });
  }, [userFavorites, allIssues]);

  // Filter and sort projects - only show open projects (null status is treated as open)
  const sortedProjects = useMemo(() => {
    if (!projects) return [];
    return [...projects]
      .filter(project => !project.status || project.status === 'open')
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  const hasFavorites = favoriteIssues.length > 0;

  // Handler to remove a favorite
  const handleRemoveFavorite = async (issueId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user?.id) return;

    await deleteRows({
      table: 'issue_favorites',
      filters: [
        { type: 'eq', column: 'user_id', value: user.id as UUID },
        { type: 'eq', column: 'issue_id', value: issueId as UUID }
      ]
    });
  };

  if (projectsLoading || issuesLoading || favoritesLoading) {
    return (
      <>
        <SidebarGroup>
          <SidebarGroupLabel>Favorites</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <Skeleton className="h-7 w-full" />
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Skeleton className="h-7 w-full" />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Projects</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <Skeleton className="h-8 w-full" />
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Skeleton className="h-8 w-full" />
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Skeleton className="h-8 w-full" />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </>
    );
  }

  if (!projects || projects.length === 0) {
    return null;
  }

  return (
    <>
      {/* Favorites section */}
      {hasFavorites && (
        <Collapsible open={expandedFavorites} onOpenChange={toggleFavorites}>
          <SidebarGroup>
            <div className="flex items-center">
              <SidebarGroupLabel className="flex-1 text-xs" data-testid="sidebar-favorites-label">Favorites</SidebarGroupLabel>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 shrink-0"
                  data-testid="sidebar-favorites-toggle"
                >
                  <ChevronRight
                    className={`h-3 w-3 transition-transform ${
                      expandedFavorites ? 'rotate-90' : ''
                    }`}
                  />
                </Button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {favoriteIssues.map(issue => {
                    const isIssueActive = location.pathname === getIssueUrl(issue.id);
                    return (
                      <SidebarMenuItem key={issue.id}>
                        <div className="flex items-center w-full group">
                          <div className="flex-1 min-w-0">
                            <IssueInlinePreview
                              issue={issue}
                              onClick={() => navigate(getIssueUrl(issue.id))}
                              isActive={isIssueActive}
                              showStatus={false}
                              className="h-7 text-xs px-2 py-1"
                              data-testid={`sidebar-favorite-${issue.id}`}
                            />
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0 ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => handleRemoveFavorite(issue.id, e)}
                                data-testid={`sidebar-favorite-remove-${issue.id}`}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Remove from favorites
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      )}

      {/* Projects section */}
      <SidebarGroup className="flex-1 overflow-auto">
        <SidebarGroupLabel className="text-xs" data-testid="sidebar-projects-label">Projects</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {sortedProjects.map(project => {
              const projectIssues = issuesByProject[project.id] || [];
              const isExpanded = expandedProjects.has(project.id);
              const hasIssues = projectIssues.length > 0;
              const isProjectActive = location.pathname === `/project/${project.id}`;

              return (
                <Collapsible
                  key={project.id}
                  open={isExpanded}
                  onOpenChange={() => toggleProject(project.id)}
                >
                  <SidebarMenuItem>
                    <div className="flex items-center w-full">
                      <SidebarMenuButton
                        asChild
                        isActive={isProjectActive}
                        tooltip={project.name}
                        className="flex-1 h-8 text-sm"
                      >
                        <a
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/project/${project.id}`);
                          }}
                          className="cursor-pointer flex items-center"
                          data-testid={`sidebar-project-${project.id}`}
                        >
                          <span className="flex-1 truncate">{project.name}</span>
                        </a>
                      </SidebarMenuButton>
                      {hasIssues && (
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 ml-1"
                            data-testid={`sidebar-project-toggle-${project.id}`}
                          >
                            <ChevronRight
                              className={`h-3 w-3 transition-transform ${
                                isExpanded ? 'rotate-90' : ''
                              }`}
                            />
                          </Button>
                        </CollapsibleTrigger>
                      )}
                    </div>
                  </SidebarMenuItem>

                  {hasIssues && (
                    <CollapsibleContent>
                      <div className="pl-2" data-testid={`sidebar-issues-container-${project.id}`}>
                        {projectIssues.map(issue => {
                          const isIssueActive = location.pathname === getIssueUrl(issue.id);
                          return (
                            <SidebarMenuItem key={issue.id}>
                              <IssueInlinePreview
                                issue={issue}
                                onClick={() => navigate(getIssueUrl(issue.id))}
                                isActive={isIssueActive}
                                showStatus={false}
                                className="h-7 text-xs px-2 py-1"
                                data-testid={`sidebar-issue-${issue.id}`}
                              />
                            </SidebarMenuItem>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  );
}
